import { AnalyticsBrowser } from '@segment/analytics-next';
import {
  areMarketingCookiesEnabled,
  trackMarketingPreferencesChange,
} from './helpers';

const config = {
  analytics: {
    dispatch: null,
    groupRestrictions: [],
    isAdobeEdu: false,
    isEnterpriseDataRestrictionsEnabled: false,
    previousMarketingCookiesEnabled: null,
    segmentWriteKey: null,
    track: null,
    globalName: 'analytics',
  },
  analyticsV4: {
    dispatch: null,
    groupRestrictions: [],
    isAdobeEdu: false,
    isEnterpriseDataRestrictionsEnabled: false,
    previousMarketingCookiesEnabled: null,
    segmentWriteKey: null,
    track: null,
    globalName: 'analyticsV4',
  },
};

const ALL_COOKIE_GROUPS = ['C0001', 'C0002', 'C0003', 'C0004'];
const DEFAULT_CONSENT_PREFERENCES = {
  C0001: true,
  C0002: false,
  C0003: false,
  C0004: false,
};

const DEFAULT_LOWERCASE_CONSENT_PREFERENCES = {
  c0001: true,
  c0002: false,
  c0003: false,
  c0004: false,
};

const eventQueue = [];

const processQueue = () => {
  eventQueue.forEach((event) => event());
  eventQueue.length = 0;
};

/**
 * @typedef {Object} EnqueueEvent
 * @property {*} event the Segment event to enqueue or run
 * @property {string} [segmentInstanceName] the Segment instance config name, defaults to 'analytics'
 * @property {boolean} [enqueueEvent] whether the tracking event should be enqueued. Useful to wait for
 *  the User to be identified for example. Defaults to `false`.
 */

/**
 * @name enqueueOrRun
 * @param {EnqueueEvent} - An object containing:
 * @description this function is used to handle scenarios where OneTrust and
 *  Segment isn't finished loading, but events are being fired off. it will
 *  either run the event now or enqueue to be ran after everything is loaded up.
 */
const enqueueOrRun = ({
  event,
  segmentInstanceName = 'analytics',
  enqueueEvent = false,
}) => {
  const segmentConfig = config[segmentInstanceName];

  if (window[segmentConfig.globalName] && !enqueueEvent) {
    event();
  } else {
    eventQueue.push(event);
  }
};

/**
 * @name configureSegmentOt
 * @param {object=} analyticsArgs - default analytics arguments. used by all clients. can be v3 or v4 segment instance.
 * @param {object=} analyticsV4Args - arguments for the second instance of segment, not required, only used by auth-portal for v4 segment instance.
 * @description updates the internal configuration of this package. refer to the default config
 * to know what needs to be updated.. it obviously has no boundaries though.
 */
function configureSegmentOt(analyticsArgs, analyticsV4Args) {
  const previousMarketingCookiesEnabled = areMarketingCookiesEnabled();
  config.analytics = {
    ...config.analytics,
    ...analyticsArgs,
    previousMarketingCookiesEnabled,
  };

  if (analyticsV4Args) {
    config.analyticsV4 = {
      ...config.analyticsV4,
      ...analyticsV4Args,
      previousMarketingCookiesEnabled,
    };
  } else {
    config.analyticsV4 = undefined;
  }
}

function getGDPRCookieCategories(sendingToMassdriver = false) {
  const activeCookieGroups = window.adobePrivacy?.activeCookieGroups();
  const returnDefaultPreferences =
    !activeCookieGroups || config.analytics.isEnterpriseDataRestrictionsEnabled;

  if (returnDefaultPreferences)
    return sendingToMassdriver
      ? DEFAULT_LOWERCASE_CONSENT_PREFERENCES
      : DEFAULT_CONSENT_PREFERENCES;

  return ALL_COOKIE_GROUPS.reduce((acc, curr) => {
    const current = sendingToMassdriver ? curr.toLowerCase() : curr;
    // handle turning off marketing for edu users
    if (curr === 'C0004' && config.analytics.isAdobeEdu) {
      acc[current] = false;
      return acc;
    }

    // turn off group restrictions
    if (config.analytics.groupRestrictions.includes(curr)) {
      acc[current] = false;
      return acc;
    }

    acc[current] = activeCookieGroups.includes(curr);
    return acc;
  }, {});
}

/**
 * @name loadSegment
 * @param {import('@segment/analytics-next').InitOptions} options - Options to pass to the _second_ argument of `AnalyticsBrowser.load`.
 * @description Loads up window.analytics (and window.analyticsV4 if additional instance args were configured) and all it's functionality.
 * NOTE: window.analytics CAN be a v4 segment instance. This function has been extended to support analyticsV4 for auth-portal, that needs to load both v3 and v4 segment instances.
 */
function loadSegment(options = {}) {
  window[config.analytics.globalName] = AnalyticsBrowser.load(
    {
      writeKey: config.analytics.segmentWriteKey,
    },
    options
  );
  if (config.analyticsV4) {
    window[config.analyticsV4.globalName] = AnalyticsBrowser.load(
      {
        writeKey: config.analyticsV4.segmentWriteKey,
      },
      options
    );
  }
}

const oldListeners = {};

/**
 * @name addHooksToOneTrustButtons
 * @param {function=} action - redux action to trigger after a button is pressed. currently used
 * to update the backend about integrations changing on the frontend
 * @param {object=} anonData - some information for anonymous users calling the backend.
 * currently requires reviewLinkId and email.
 * @description Adds event listeners to all of the necessary buttons in the adobe privacy
 * preferences menu
 */

function addHooksToOneTrustButtons(action, anonData) {
  const events = {
    privacyConsent: 'adobePrivacy:PrivacyConsent',
    privacyConsentCustom: 'adobePrivacy:PrivacyCustom',
    privacyReject: 'adobePrivacy:PrivacyReject',
  };
  let listener;

  Object.values(events).forEach((eventName) => {
    window.removeEventListener(eventName, oldListeners[eventName], true);
    listener = () => {
      setTimeout(() => {
        const cookieCategories = getGDPRCookieCategories(true);
        const marketingCookiesEnabled = areMarketingCookiesEnabled();

        trackMarketingPreferencesChange(
          config.analytics,
          marketingCookiesEnabled
        );
        config.analytics.previousMarketingCookiesEnabled =
          marketingCookiesEnabled;

        if (config.analyticsV4) {
          trackMarketingPreferencesChange(
            config.analyticsV4,
            marketingCookiesEnabled
          );
          config.analyticsV4.previousMarketingCookiesEnabled =
            marketingCookiesEnabled;
        }

        if (!action) return;
        if (anonData) {
          const { reviewLinkId, email } = anonData;
          config.analytics.dispatch(
            action(cookieCategories, reviewLinkId, email)
          );
        } else {
          config.analytics.dispatch(action(cookieCategories));
        }
      }, 1000);
    };

    oldListeners[eventName] = listener;
    window.addEventListener(eventName, listener, true);
  });
}

/**
 * @name formatOneTrustElements
 * @param { string } horizontalAlignment - can pass in an alignment to move banner around
 * @description Formats all of the styling for the OneTrust banner, modal, and confirmation.
 * Overrides any existing styling set by the adobe script.
 */
function formatOneTrustElements(horizontalAlignment = 'right') {
  const css = `@media (min-width: 960px) {
    #onetrust-consent-sdk #onetrust-banner-sdk.otFlat.vertical-align-content {
      ${horizontalAlignment}: 50px !important;
      bottom: 50px !important;
      max-width: 450px;
    }

    #onetrust-pc-btn-handler.cookie-setting-link {
      width: auto;
    }

    #onetrust-consent-sdk #onetrust-button-group {
      text-align: right;
    }
  }

  #onetrust-consent-sdk #onetrust-banner-sdk.otFlat.vertical-align-content {
    position: fixed;
    left: unset;
    right: 0;
    bottom: 0;
    padding: 1.5rem;
    border-radius: 2rem;
    background-color: #fcfcfc;
  }

  #onetrust-consent-sdk
    #onetrust-banner-sdk.otFlat.vertical-align-content
    .ot-sdk-container {
    padding: 0 !important;
  }

  #onetrust-banner-sdk .ot-sdk-row,
  #onetrust-consent-sdk #onetrust-banner-sdk .ot-sdk-container .ot-sdk-row {
    padding: 0 !important;
  }

  #onetrust-consent-sdk
    #onetrust-banner-sdk.otFlat.vertical-align-content
    #onetrust-policy {
    margin: 0;
  }

  #onetrust-consent-sdk
    #onetrust-banner-sdk.otFlat.vertical-align-content
    #onetrust-policy-title {
    margin-bottom: 0;
    color: black !important;
    font-size: 17px;
  }

  #onetrust-consent-sdk
    #onetrust-banner-sdk.otFlat.vertical-align-content
    #onetrust-policy-text {
    margin-top: 16px !important;
    margin-bottom: 16px !important;
    color: black;
    font-size: 12px;
  }

  /* Buttons Container */
  #onetrust-consent-sdk
    #onetrust-banner-sdk.otFlat.vertical-align-content
    #onetrust-button-group {
    margin-top: 0;
    margin-bottom: 0;
    text-align: left;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: repeat(2, 1fr);
    column-gap: 12px;
    row-gap: 12px;
  }

  /* buttons */
  #onetrust-button-group #onetrust-pc-btn-handler {
    color: black !important;
    background-color: #fcfcfc !important;
    height: 32px !important;
    padding: 1px 20px 0px 20px !important;
    border: 2px solid black !important;
    margin: 0 !important;
    border-radius: 100px !important;
    font-style: normal !important;
    font-weight: 600 !important;
    font-size: 12px !important;
    letter-spacing: 0 !important;
    line-height: 0.9 !important;
    width: 100%;
    grid-column: span 2;
  }

  #onetrust-button-group #onetrust-pc-btn-handler:hover {
    color: #fcfcfc !important;
    background-color: black !important;
  }

  #onetrust-button-group #onetrust-reject-all-handler,
  #onetrust-button-group #onetrust-accept-btn-handler {
    color: #fcfcfc !important;
    background-color: black !important;
    height: 32px !important;
    padding: 1px 20px 0px 20px !important;
    border: 2px solid black !important;
    margin: 0 !important;
    border-radius: 100px !important;
    font-style: normal !important;
    font-weight: 600 !important;
    font-size: 12px !important;
    letter-spacing: 0 !important;
    line-height: 0.9 !important;
  }

  #onetrust-button-group #onetrust-reject-all-handler:hover,
  #onetrust-button-group #onetrust-accept-btn-handler:hover {
    color: black !important;
    background-color: #fcfcfc !important;
  }

  /* Accepted prompt */
  @media (min-width: 960px) {
    #ot-cookie-settings .cs-container {
      background-color: #5B53FF;
      width: auto !important;
      padding: 12px 20px !important;
    }
  }

  #ot-cookie-settings .cs-container {
    background-color: #5B53FF;
    width: 488px !important;
    border-radius: 50px !important;
    padding: 12px 20px !important;
  }

  #ot-cookie-settings .cs-text,
  #ot-cookie-settings .cs-settings-text,
  #ot-cookie-settings .cs-close {
    color: #fcfcfc;
    font-size: 14px !important;
    font-style: normal !important;
    font-weight: 400 !important;
    letter-spacing: 0 !important;
    line-height: 1.6 !important;
  }

  #ot-cookie-settings .cs-settings-text {
    font-weight: 600 !important;
  }

  /* One trust config modal */
  @media (min-width: 960) {
    #onetrust-consent-sdk #onetrust-pc-sdk.ot-sdk-container #pc-title {
      max-width: 70% !important;
    }
    #onetrust-consent-sdk
      #onetrust-pc-sdk.ot-sdk-container
      #pc-policy-text
      #ot-pc-logo-button
      .pc-save-and-close {
      margin-left: 16px !important;
    }
  }

  #onetrust-consent-sdk #onetrust-pc-sdk.ot-sdk-container {
    border-radius: 20px !important;
    max-width: 975px !important;
  }

  #onetrust-consent-sdk
    #onetrust-pc-sdk.ot-sdk-container
    #ot-content.ot-main-content {
    color: black;
  }

  /* title2 */
  #onetrust-consent-sdk #onetrust-pc-sdk.ot-sdk-container #pc-title {
    font-size: 38px !important;
    font-style: normal !important;
    font-weight: 600 !important;
    letter-spacing: 0 !important;
    line-height: 1.15 !important;
    color: black;
  }

  /* right side buttons */
  /* it looks like there is one pixel difference between the three buttons */
  #onetrust-consent-sdk
    #onetrust-pc-sdk.ot-sdk-container
    #pc-policy-text
    #ot-pc-logo-button
    .disable-all-btn,
  #onetrust-consent-sdk
    #onetrust-pc-sdk.ot-sdk-container
    #pc-policy-text
    #ot-pc-logo-button
    .pc-save-and-close,
  #onetrust-consent-sdk
    #onetrust-pc-sdk.ot-sdk-container
    #pc-policy-text
    #ot-pc-logo-button
    .enable-all-btn {
    height: 32px !important;
    padding: 1px 20px 0px 20px !important;
    border: 2px solid black !important;
    margin: 0px 4px !important;
    border-radius: 100px !important;
    font-size: 12px !important;
    font-style: normal !important;
    font-weight: 500 !important;
    letter-spacing: 0 !important;
    line-height: 0.9 !important;
  }

  #onetrust-consent-sdk
    #onetrust-pc-sdk.ot-sdk-container
    #pc-policy-text
    #ot-pc-logo-button
    .disable-all-btn,
  #onetrust-consent-sdk
    #onetrust-pc-sdk.ot-sdk-container
    #pc-policy-text
    #ot-pc-logo-button
    .pc-save-and-close {
    background-color: black !important;
    color: #fcfcfc !important;
  }

  #onetrust-consent-sdk
    #onetrust-pc-sdk.ot-sdk-container
    #pc-policy-text
    #ot-pc-logo-button
    .enable-all-btn {
    background-color: #fcfcfc !important;
    color: black !important;
  }

  #onetrust-consent-sdk
    #onetrust-pc-sdk.ot-sdk-container
    #pc-policy-text
    #ot-pc-logo-button
    .disable-all-btn:hover,
  #onetrust-consent-sdk
    #onetrust-pc-sdk.ot-sdk-container
    #pc-policy-text
    #ot-pc-logo-button
    .pc-save-and-close:hover {
    background-color: #fcfcfc !important;
    color: black !important;
  }

  #onetrust-consent-sdk
    #onetrust-pc-sdk.ot-sdk-container
    #pc-policy-text
    #ot-pc-logo-button
    .enable-all-btn:hover {
    background-color: black !important;
    color: #fcfcfc !important;
  }

  /* OT general */
  #onetrust-consent-sdk
    #onetrust-pc-sdk.ot-sdk-container
    #pc-policy-text
    .ot-general {
    color: black !important;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1) !important;
  }

  /* title3 */
  #onetrust-consent-sdk
    #onetrust-pc-sdk.ot-sdk-container
    #pc-policy-text
    .ot-general
    h3 {
    font-size: 30px !important;
    font-style: normal !important;
    font-weight: 600 !important;
    letter-spacing: 0 !important;
    line-height: 1.2 !important;
    color: black !important;
  }

  #onetrust-consent-sdk
    #onetrust-pc-sdk.ot-sdk-container
    #pc-policy-text
    .ot-general
    .ot-general-width
    li
    /* Accordion title6 */
    .ot-general-question {
    font-size: 17px !important;
    font-style: normal !important;
    font-weight: 600 !important;
    letter-spacing: 0 !important;
    line-height: 1.2 !important;
    color: black !important;
  }

  /* bodySmall */
  #onetrust-consent-sdk
    #onetrust-pc-sdk.ot-sdk-container
    #pc-policy-text
    .ot-general
    .ot-general-width
    li
    p {
    color: black !important;
    font-size: 15px !important;
    font-style: normal !important;
    font-weight: 400 !important;
    letter-spacing: 0 !important;
    line-height: 1.4 !important;
  }

  /* enable/disabled text */
  #onetrust-consent-sdk
    #onetrust-pc-sdk.ot-sdk-container
    #pc-policy-text
    .ot-enable-disabled {
    padding-top: 20px;
    color: black !important;
  }

  #onetrust-consent-sdk
    #onetrust-pc-sdk.ot-sdk-container
    #pc-policy-text
    .ot-enable-disabled
    .ot-disable,
  #onetrust-consent-sdk
    #onetrust-pc-sdk.ot-sdk-container
    #pc-policy-text
    .ot-enable-disabled
    .ot-enable {
    border: none !important;
  }

  /* title3 */
  #onetrust-consent-sdk
    #onetrust-pc-sdk.ot-sdk-container
    #pc-policy-text
    .ot-enable-disabled
    .ot-ed-title {
    font-size: 30px !important;
    font-style: normal !important;
    font-weight: 600 !important;
    letter-spacing: 0 !important;
    line-height: 1.2 !important;
    color: black !important;
  }

  /* bodySmall */
  #onetrust-consent-sdk
    #onetrust-pc-sdk.ot-sdk-container
    #pc-policy-text
    .ot-enable-disabled
    ul
    li {
    color: black !important;
    font-size: 15px !important;
    font-style: normal !important;
    font-weight: 400 !important;
    letter-spacing: 0 !important;
    line-height: 1.4 !important;
  }

  @media (min-width: 960px) {
    #onetrust-consent-sdk
      #onetrust-pc-sdk.ot-sdk-container
      #cookie-preferences
      .category-item {
      margin-top: 32px !important;
    }
  }

  /* actual cookie preferences */
  #onetrust-consent-sdk
    #onetrust-pc-sdk.ot-sdk-container
    #cookie-preferences
    .category-item {
    margin-top: 16px !important;
  }
  /* title5 */
  #onetrust-consent-sdk
    #onetrust-pc-sdk.ot-sdk-container
    #cookie-preferences
    .category-item
    .category-header {
    color: black !important;
    font-size: 22px !important;
    font-style: normal !important;
    font-weight: 600 !important;
    letter-spacing: 0 !important;
    line-height: 1.33 !important;
    margin-bottom: 8px !important;
  }

  #onetrust-consent-sdk
    #onetrust-pc-sdk.ot-sdk-container
    #cookie-preferences
    .category-item
    .ot-always-active {
    color: black !important;
  }

  /* bodySmall */
  #onetrust-consent-sdk
    #onetrust-pc-sdk.ot-sdk-container
    #cookie-preferences
    .category-item
    .ot-category-desc,
  #onetrust-consent-sdk
    #onetrust-pc-sdk.ot-sdk-container
    #cookie-preferences
    .category-item
    .ot-always-active {
    color: black !important;
    font-size: 15px !important;
    font-style: normal !important;
    font-weight: 400 !important;
    letter-spacing: 0 !important;
    line-height: 1.4 !important;
  }

  /* textLink */
  #onetrust-consent-sdk
    #onetrust-pc-sdk.ot-sdk-container
    #cookie-preferences
    .category-item
    .category-host-list-handler {
    color: black !important;
    font-size: 14px !important;
    font-style: normal !important;
    font-weight: 600 !important;
    letter-spacing: 0 !important;
    line-height: 0.96 !important;
    text-decoration: underline !important;
  }

  #onetrust-consent-sdk
    #onetrust-pc-sdk.ot-sdk-container
    #cookie-preferences
    .category-item
    .ot-switch-label:before {
    border-color: black;
  }
  #onetrust-consent-sdk
    #onetrust-pc-sdk.ot-sdk-container
    #cookie-preferences
    .category-item
    .ot-switch
    input[type='checkbox']:checked
    + .ot-switch-label::before {
    background-color: black;
  }

  `;

  const styletag = document.createElement('style');
  styletag.innerHTML = css;
  document.head.appendChild(styletag);
}

export {
  addHooksToOneTrustButtons,
  configureSegmentOt,
  enqueueOrRun,
  formatOneTrustElements,
  getGDPRCookieCategories,
  loadSegment,
  processQueue,
};
